import dayjs from "dayjs";
import { FIXED_MAXIMUM_INVESTMENT_AMOUNT } from "config/envConfig";
import { uploadItemHasUrl } from "utils/uploadFormItems";

const PLAIN_TEXT = (html = "") =>
  html.replaceAll(/<[^>]*>/g, "").replaceAll("&nbsp;", " ").trim();

const MIN_RICH_TEXT_LENGTH = 20;
const MAX_BASICS_TEXT_LENGTH = 200;

const URL_RE = /^https?:\/\/.+\..+/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Inline + SweetAlert when any selected file/media row is not uploaded yet. */
export const UPLOAD_ALL_FILES_MESSAGE =
  "Upload every selected file using the upload icon on each row before continuing. All rows must finish uploading.";

function parseYmdStrict(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const d = dayjs(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d.startOf("day") : null;
}

function todayStart() {
  return dayjs().startOf("day");
}

export function parseCurrencyInput(raw) {
  if (raw == null) return Number.NaN;
  const s = String(raw).replaceAll(",", "").trim();
  if (s === "") return Number.NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NaN;
}

export function formatCurrencyDisplay(value) {
  if (value === "" || value == null) return "";
  const n = parseCurrencyInput(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Maximum investment is fixed in the UI; always resolve a numeric cap for comparisons. */
function resolveMaximumInvestment(values) {
  const parsed = parseCurrencyInput(values.maximumInvestmentAmount);
  const fixed = parseCurrencyInput(FIXED_MAXIMUM_INVESTMENT_AMOUNT);
  if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
  return Number.isNaN(fixed) ? 2500 : fixed;
}

function richTextRequiredMessage(fieldLabel, plainLength) {
  if (plainLength === 0) {
    return `${fieldLabel} is required.`;
  }
  return `${fieldLabel} must be at least ${MIN_RICH_TEXT_LENGTH} characters.`;
}

function validateRequiredTrimmedField(errors, key, value, { required, tooLong = null }) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    errors[key] = required;
    return;
  }
  if (tooLong && trimmed.length > MAX_BASICS_TEXT_LENGTH) {
    errors[key] = tooLong;
  }
}

function validateBasicsTextFields(values, errors) {
  validateRequiredTrimmedField(errors, "title", values.title, {
    required: "Project title is required.",
  });
  validateRequiredTrimmedField(errors, "issuerName", values.issuerName, {
    required: "Issuer name is required.",
    tooLong: "Issuer name must be 200 characters or fewer.",
  });
  validateRequiredTrimmedField(errors, "typeOfEligibleSecurityOffered", values.typeOfEligibleSecurityOffered, {
    required: "Type of eligible security offered is required.",
    tooLong: "Type of eligible security offered must be 200 characters or fewer.",
  });

  if (!values.status?.trim()) {
    errors.status = "Select a status.";
  }
  if (!values.industry) {
    errors.industry = "Please select an industry.";
  }
}

function validateCampaignDates(values, errors) {
  if (!values.campaignStartAt) {
    errors.campaignStartAt = "Campaign start date is required.";
  }
  if (!values.campaignEndAt) {
    errors.campaignEndAt = "Campaign end date is required.";
  }

  const startD = parseYmdStrict(values.campaignStartAt);
  const endD = parseYmdStrict(values.campaignEndAt);
  const today = todayStart();

  if (values.campaignStartAt && !startD) {
    errors.campaignStartAt = "Enter a valid campaign start date.";
  }
  if (values.campaignEndAt && !endD) {
    errors.campaignEndAt = "Enter a valid campaign end date.";
  }
  if (startD?.isBefore(today, "day")) {
    errors.campaignStartAt = "Campaign start cannot be before today.";
  }
  if (endD?.isBefore(today, "day")) {
    errors.campaignEndAt = "Campaign end cannot be before today.";
  }
  if (startD != null && endD?.isBefore(startD, "day")) {
    errors.campaignEndAt = "End date must be on or after the start date.";
  }
}

function parseBasicsAmounts(values) {
  const invMax = resolveMaximumInvestment(values);
  return {
    totalFundsRaw: String(values.totalFundsRequired ?? "").trim(),
    hardCapRaw: String(values.hardCap ?? "").trim(),
    invMinRaw: String(values.minimumInvestmentAmount ?? "").trim(),
    sharePriceRaw: String(values.sharePrice ?? "").trim(),
    totalFunds: parseCurrencyInput(values.totalFundsRequired),
    hardCap: parseCurrencyInput(values.hardCap),
    invMin: parseCurrencyInput(values.minimumInvestmentAmount),
    invMax,
    invMaxLabel: formatCurrencyDisplay(String(invMax)),
    price: parseCurrencyInput(values.sharePrice),
  };
}

function validateNonNegativeCurrencyField(errors, key, raw, parsed, messages) {
  if (!raw) {
    errors[key] = messages.required;
    return;
  }
  if (Number.isNaN(parsed) || parsed < 0) {
    errors[key] = messages.invalid;
  }
}

function validatePositiveCurrencyField(errors, key, raw, parsed, messages) {
  if (!raw) {
    errors[key] = messages.required;
    return;
  }
  if (Number.isNaN(parsed) || parsed <= 0) {
    errors[key] = messages.invalid;
  }
}

function validateBasicsCurrencyFields(errors, amounts) {
  const {
    totalFundsRaw,
    hardCapRaw,
    invMinRaw,
    sharePriceRaw,
    totalFunds,
    hardCap,
    invMin,
    price,
  } = amounts;

  validateNonNegativeCurrencyField(errors, "totalFundsRequired", totalFundsRaw, totalFunds, {
    required: "Total funds required is required.",
    invalid: "Enter a valid total funds required.",
  });
  validateNonNegativeCurrencyField(errors, "hardCap", hardCapRaw, hardCap, {
    required: "Hard cap is required.",
    invalid: "Enter a valid hard cap.",
  });
  validateNonNegativeCurrencyField(errors, "minimumInvestmentAmount", invMinRaw, invMin, {
    required: "Minimum investment per investor is required.",
    invalid: "Enter a valid minimum investment per investor.",
  });
  validatePositiveCurrencyField(errors, "sharePrice", sharePriceRaw, price, {
    required: "Share price is required.",
    invalid: "Enter a valid share price.",
  });
}

function validateInvestmentCrossRules(errors, amounts) {
  const { totalFunds, hardCap, invMin, invMax, invMaxLabel } = amounts;

  if (
    !errors.totalFundsRequired &&
    !errors.hardCap &&
    !Number.isNaN(totalFunds) &&
    !Number.isNaN(hardCap) &&
    hardCap < totalFunds
  ) {
    errors.hardCap = "Hard cap must be greater than or equal to total funds required.";
  }

  if (!errors.minimumInvestmentAmount && !Number.isNaN(invMin) && invMin > invMax) {
    errors.minimumInvestmentAmount = `Minimum investment cannot exceed the maximum per investor ($${invMaxLabel}).`;
  }

  if (!errors.hardCap && !Number.isNaN(hardCap) && invMax > hardCap) {
    errors.hardCap = `Hard cap must be at least the maximum investment per investor ($${invMaxLabel}).`;
  }

  if (
    !errors.minimumInvestmentAmount &&
    !Number.isNaN(hardCap) &&
    !Number.isNaN(invMin) &&
    invMin > hardCap
  ) {
    errors.minimumInvestmentAmount = "Minimum investment cannot exceed the campaign hard cap.";
  }
}

function validateSharePriceCrossRules(errors, amounts) {
  const { totalFunds, hardCap, invMin, price } = amounts;

  if (errors.sharePrice || Number.isNaN(price) || price <= 0) {
    return;
  }

  if (!errors.totalFundsRequired && !Number.isNaN(totalFunds) && totalFunds <= price) {
    errors.totalFundsRequired = "Total funds required must be greater than the share price.";
  }
  if (!errors.hardCap && !Number.isNaN(hardCap) && hardCap <= price) {
    errors.hardCap = "Hard cap must be greater than the share price.";
  }
  if (!errors.minimumInvestmentAmount && !Number.isNaN(invMin) && invMin <= price) {
    errors.minimumInvestmentAmount =
      "Minimum investment per investor must be greater than the share price.";
  }
}

/** Project details card (visible on every step). */
export function validateProjectBasics(values, errors) {
  validateBasicsTextFields(values, errors);
  validateCampaignDates(values, errors);

  const amounts = parseBasicsAmounts(values);
  validateBasicsCurrencyFields(errors, amounts);
  validateInvestmentCrossRules(errors, amounts);
  validateSharePriceCrossRules(errors, amounts);
}

function validateOverviewTabContent(values, errors) {
  const descriptionPlain = PLAIN_TEXT(values.description);
  if (descriptionPlain.length < MIN_RICH_TEXT_LENGTH) {
    errors.description = richTextRequiredMessage("Project description", descriptionPlain.length);
  }

  const mediaItems = Array.isArray(values.imageUrl) ? values.imageUrl : [];
  if (mediaItems.length === 0) {
    errors.imageUrl = "Add at least one project image or video file.";
    return;
  }
  if (!mediaItems.every((it) => uploadItemHasUrl(it))) {
    errors.imageUrl = UPLOAD_ALL_FILES_MESSAGE;
  }
}

function validateOverview(values, errors) {
  validateProjectBasics(values, errors);
  validateOverviewTabContent(values, errors);
}

function validateCompany(values, errors) {
  const website = String(values.companyWebsite ?? "").trim();
  if (!website) {
    errors.companyWebsite = "Company website is required.";
  } else if (!URL_RE.test(website)) {
    errors.companyWebsite = "Enter a valid URL starting with http:// or https://.";
  }

  const email = String(values.companyEmail ?? "").trim();
  if (!email) {
    errors.companyEmail = "Company email is required.";
  } else if (!EMAIL_RE.test(email)) {
    errors.companyEmail = "Enter a valid company email address.";
  }

  const phone = String(values.companyPhone ?? "").trim();
  if (!phone) {
    errors.companyPhone = "Company phone number is required.";
  }

  const address = String(values.companyHeadOfficeAddress ?? "").trim();
  if (!address) {
    errors.companyHeadOfficeAddress = "Head office address is required.";
  }

  const companyBody = PLAIN_TEXT(values.companyOverview);
  if (companyBody.length < MIN_RICH_TEXT_LENGTH) {
    errors.companyOverview = richTextRequiredMessage("Company overview", companyBody.length);
  }
}

function memberStableId(member, index) {
  const id = String(member?.id ?? "").trim();
  return id || `member-${index}`;
}

export function validateTeamMemberFields(member) {
  const me = {};

  if (!String(member?.name ?? "").trim()) {
    me.name = "Name is required.";
  }
  if (!String(member?.position ?? "").trim()) {
    me.position = "Position at issuer is required.";
  }
  if (!String(member?.municipality ?? "").trim()) {
    me.municipality = "Municipality of residence is required.";
  }

  const email = String(member?.email ?? "").trim();
  if (!email) {
    me.email = "Email address is required.";
  } else if (!EMAIL_RE.test(email)) {
    me.email = "Enter a valid email address.";
  }

  if (!String(member?.occupation ?? "").trim()) {
    me.occupation = "Principal occupation is required.";
  }

  const aboutText = PLAIN_TEXT(member?.description ?? "");
  if (aboutText.length < MIN_RICH_TEXT_LENGTH) {
    me.description = richTextRequiredMessage("About section", aboutText.length);
  }

  const imageUrl = String(member?.imageUrl ?? "").trim();
  if (member?.imagePending && !imageUrl) {
    me.imageUrl = UPLOAD_ALL_FILES_MESSAGE;
  } else if (!imageUrl) {
    me.imageUrl = "Team member image is required.";
  }

  return me;
}

function validateTeam(values, errors) {
  const members = Array.isArray(values.teamMembers) ? values.teamMembers : [];
  if (members.length === 0) {
    errors.teamMembers = "Add at least one team member.";
    return;
  }

  const teamMemberErrorsById = {};
  const memberErrors = members.map((member, index) => {
    const fieldErrors = validateTeamMemberFields(member);
    if (Object.keys(fieldErrors).length === 0) {
      return null;
    }
    teamMemberErrorsById[memberStableId(member, index)] = fieldErrors;
    return fieldErrors;
  });

  if (memberErrors.some(Boolean)) {
    errors.teamMemberErrors = memberErrors;
    errors.teamMemberErrorsById = teamMemberErrorsById;
  }
}

function newsUpdateStableId(item, index) {
  const id = String(item?.id ?? "").trim();
  return id || `news-${index}`;
}

export function validateNewsUpdateFields(item) {
  const fieldErrors = {};

  if (!String(item?.updateTitle ?? "").trim()) {
    fieldErrors.updateTitle = "Title is required.";
  }

  if (!String(item?.date ?? "").trim()) {
    fieldErrors.date = "Date is required.";
  } else if (!parseYmdStrict(item.date)) {
    fieldErrors.date = "Enter a valid date.";
  }

  const descriptionPlain = PLAIN_TEXT(item?.updateDescription ?? "");
  if (descriptionPlain.length < MIN_RICH_TEXT_LENGTH) {
    fieldErrors.updateDescription = richTextRequiredMessage("Description", descriptionPlain.length);
  }

  return fieldErrors;
}

function validateNewsUpdates(values, errors) {
  const items = Array.isArray(values.newsUpdates) ? values.newsUpdates : [];
  if (items.length === 0) {
    errors.newsUpdates = "Add at least one news & updates entry.";
    return;
  }

  const newsUpdateErrorsById = {};
  const itemErrors = items.map((item, index) => {
    const fieldErrors = validateNewsUpdateFields(item);
    if (Object.keys(fieldErrors).length === 0) {
      return null;
    }
    newsUpdateErrorsById[newsUpdateStableId(item, index)] = fieldErrors;
    return fieldErrors;
  });

  if (itemErrors.some(Boolean)) {
    errors.newsUpdateErrors = itemErrors;
    errors.newsUpdateErrorsById = newsUpdateErrorsById;
  }
}

function validateOffering(values, errors) {
  const docs = Array.isArray(values.offeringDocuments) ? values.offeringDocuments : [];
  const meaningful = docs.filter(
    (d) => String(d?.name ?? "").trim() || String(d?.url ?? "").trim(),
  );
  if (meaningful.length === 0) {
    errors.offeringDocuments = "Upload at least one offering document (PDF or Word).";
    return;
  }
  const allUploaded = meaningful.every(
    (d) => String(d?.url ?? "").trim() && String(d?.name ?? "").trim(),
  );
  if (!allUploaded) {
    errors.offeringDocuments = meaningful.some((d) => !String(d?.url ?? "").trim())
      ? UPLOAD_ALL_FILES_MESSAGE
      : "Each offering document must have a name.";
  }
}

const STEP_VALIDATORS = {
  overview: validateOverview,
  company: validateCompany,
  team: validateTeam,
  offering: validateOffering,
  newsUpdates: validateNewsUpdates,
};

export function validateProjectStep(stepId, values) {
  const errors = {};
  STEP_VALIDATORS[stepId]?.(values, errors);
  return errors;
}

/** Current step plus shared project details card (shown on every tab). */
export function validateProjectStepForNavigation(stepId, values) {
  const errors = validateProjectStep(stepId, values);
  if (stepId === "overview") {
    return errors;
  }
  const basicsErrors = {};
  validateProjectBasics(values, basicsErrors);
  return { ...basicsErrors, ...errors };
}

function overviewHasIncompleteUploads(values) {
  const items = Array.isArray(values.imageUrl) ? values.imageUrl : [];
  return items.length > 0 && !items.every((it) => uploadItemHasUrl(it));
}

function offeringHasIncompleteUploads(values) {
  const docs = Array.isArray(values.offeringDocuments) ? values.offeringDocuments : [];
  const meaningful = docs.filter(
    (d) => String(d?.name ?? "").trim() || String(d?.url ?? "").trim(),
  );
  return meaningful.length > 0 && !meaningful.every((d) => String(d?.url ?? "").trim());
}

function teamHasIncompleteUploads(values) {
  const members = Array.isArray(values.teamMembers) ? values.teamMembers : [];
  return members.some((m) => Boolean(m.imagePending && !m.imageUrl));
}

const INCOMPLETE_UPLOAD_CHECKERS = {
  overview: overviewHasIncompleteUploads,
  offering: offeringHasIncompleteUploads,
  team: teamHasIncompleteUploads,
};

/** True when the user added file rows (or offering rows) but at least one is not uploaded yet. */
export function projectStepHasIncompleteFileSelection(stepId, values) {
  const check = INCOMPLETE_UPLOAD_CHECKERS[stepId];
  return check ? check(values) : false;
}
